/**
 * Photos from the partner's phone (Warren, 21 October 2026: as many as needed, compressed on upload, each with its
 * time stamp and location). The phone shrinks each photo to at most 1600 pixels on its long edge as a JPEG before it
 * leaves, so a site full of photos uploads over a weak signal. The time stamp is when the photo was taken; the
 * location is the phone's position then, when the partner allows it.
 */
export const PHOTO_MAX_EDGE = 1600;
export const PHOTO_QUALITY = 0.8;

export interface CapturedPhoto {
  blob: Blob; width: number; height: number; taken_at: string;
  location: { lat: number; lng: number; accuracy_m: number | null } | null; client_id: string;
}

/** The scaled size that keeps the aspect ratio and fits the long edge. */
export function fitWithin(width: number, height: number, max = PHOTO_MAX_EDGE) {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export const newClientId = () => `ph-${Date.now().toString(36)}-${crypto.getRandomValues(new Uint32Array(2)).join('')}`;

export async function compressImage(file: Blob): Promise<{ blob: Blob; width: number; height: number }> {
  // createImageBitmap honours the photo's EXIF orientation, so a portrait photo stays upright.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot prepare photos.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY));
  if (!blob) throw new Error('The photo could not be prepared.');
  return { blob, width, height };
}

/** The phone's position, or null when the partner declines or the phone cannot tell within a few seconds. */
export function currentPosition(timeoutMs = 6000): Promise<CapturedPhoto['location']> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: Number.isFinite(p.coords.accuracy) ? Math.round(p.coords.accuracy) : null }),
      () => resolve(null), { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 });
  });
}

/** One photo from a file input: compressed, stamped with when it was taken (the file's own time from a camera) and where. */
export async function capturePhoto(file: File): Promise<CapturedPhoto> {
  const [img, location] = await Promise.all([compressImage(file), currentPosition()]);
  const takenMs = file.lastModified && Math.abs(Date.now() - file.lastModified) < 7 * 86400000 ? file.lastModified : Date.now();
  return { ...img, taken_at: new Date(takenMs).toISOString(), location, client_id: newClientId() };
}
